import React, { ReactNode, useState } from 'react';
import { Label as AriaLabel } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';
import { Alpha2Code } from 'i18n-iso-countries';

import { InputCountry } from '@/components/InputCountry/InputCountry';
import Box from '@/design_system/Box';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { AddressRaw } from '@/models/address';
import { createBEMClasses } from '@/utils/classname';
import { isEmailValid } from '@/utils/email';

import './AddressForm.css';

const { block, element } = createBEMClasses('address-form');

type AddressDisabledFields = {
  contactName?: boolean;
  contactEmail?: boolean;
  line1?: boolean;
  line2?: boolean;
  zipCode?: boolean;
  city?: boolean;
  state?: boolean;
  country?: boolean;
};

const AddressForm = ({
  label,
  contact = true,
  size = 'medium',
  variant = 'default',
  value,
  onChange,
  error,
  isDisabled,
  disabledFields,
}: {
  label?: ReactNode;
  contact?: boolean;
  size?: 'medium' | 'large';
  variant?: 'default' | 'brand';
  value: Partial<AddressRaw> | null;
  onChange?: (address: Partial<AddressRaw> | null) => void;
  error?: AddressError;
  isDisabled?: boolean;
  disabledFields?: AddressDisabledFields;
}) => {
  const { t } = useLingui();

  const onFieldsChange = (fields: Partial<AddressRaw>) => {
    onChange?.({ ...value, ...fields });
  };

  const showState = !!value?.country && COUNTRIES_REQUIRING_STATE.includes(value.country);

  const isFieldDisabled = (field: keyof AddressDisabledFields) =>
    !!isDisabled || !!disabledFields?.[field];

  return (
    <Stack gap="1.5rem" className={block()}>
      {contact && (
        <Stack row gap="0.5rem">
          <InputText
            style={{ flex: 1 }}
            aria-label={t({ id: 'components.address-form.contact.label', message: 'Contact name' })}
            label={t({
              id: 'components.address-form.contact.label',
              message: 'Contact name',
            })}
            size={size}
            type="text"
            placeholder={t({
              id: 'components.address-form.contact.label',
              message: 'Contact name',
            })}
            value={value?.contactName ?? ''}
            onChange={(contactName) => onFieldsChange({ contactName })}
            autoComplete="name"
            isDisabled={isFieldDisabled('contactName')}
            error={
              error?.contactNameError
                ? t({
                    id: 'components.address-form.field.contact-name.error',
                    message: 'Please add a name',
                  })
                : undefined
            }
          />
          <InputText
            style={{ flex: 1 }}
            aria-label={t({
              id: 'components.address-form.contact-email.label',
              message: 'Contact email',
            })}
            label={t({
              id: 'components.address-form.contact-email.label',
              message: 'Contact email',
            })}
            size={size}
            type="text"
            placeholder={t({
              id: 'components.address-form.contact-email.label',
              message: 'Contact email',
            })}
            value={value?.contactEmail ?? ''}
            onChange={(contactEmail) => onFieldsChange({ contactEmail })}
            autoComplete="email"
            isDisabled={isFieldDisabled('contactEmail')}
            error={
              error?.contactEmailError
                ? t({
                    id: 'components.address-form.field.contact-email.error',
                    message: 'Please add a valid email',
                  })
                : undefined
            }
          />
        </Stack>
      )}
      <Stack gap="0.25rem">
        {/* TODO: remove this classname by adding a proper label component with size and weight */}
        <AriaLabel className={size === 'large' ? 'paragraph-50-medium' : 'paragraph-100-medium'}>
          {label ?? <Trans id="components.address-form.label">Address</Trans>}
        </AriaLabel>

        <Box>
          <Stack gap="0.5rem">
            <InputText
              label={t({
                id: 'components.address-form.street-address.label',
                message: 'Street address',
              })}
              size={size}
              type="text"
              aria-label={t({
                id: 'components.address-form.line-1.label',
                message: 'Address line 1',
              })}
              placeholder={t({
                id: 'components.address-form.line-1.label',
                message: 'Address line 1',
              })}
              value={value?.line1 ?? ''}
              onChange={(line1) => onFieldsChange({ line1 })}
              autoComplete="address-line1"
              isDisabled={isFieldDisabled('line1')}
              error={
                error?.line1Error
                  ? t({
                      id: 'components.address-form.field.address-line1.error',
                      message: 'Please add an address',
                    })
                  : undefined
              }
            />

            <InputText
              size={size}
              type="text"
              aria-label={t({
                id: 'components.address-form.line-2.label',
                message: 'Address line 2',
              })}
              placeholder={t({
                id: 'components.address-form.line-2.label',
                message: 'Address line 2',
              })}
              value={value?.line2 ?? ''}
              onChange={(line2) => onFieldsChange({ line2 })}
              autoComplete="address-line2"
              isDisabled={isFieldDisabled('line2')}
            />

            <div className={element('zip-city-country')}>
              <InputText
                label={t({ id: 'components.address-form.zip.label', message: 'Zip code' })}
                size={size}
                type="text"
                aria-label={t({ id: 'components.address-form.zip.label', message: 'Zip code' })}
                placeholder={t({ id: 'components.address-form.zip.label', message: 'Zip code' })}
                value={value?.zipCode ?? ''}
                onChange={(zipCode) => onFieldsChange({ zipCode })}
                autoComplete="postal-code"
                isDisabled={isFieldDisabled('zipCode')}
                error={
                  error?.zipCodeError
                    ? t({
                        id: 'components.address-form.field.zip-code.error',
                        message: 'Please add a zip code',
                      })
                    : undefined
                }
              />

              <InputText
                label={t({ id: 'components.address-form.city.label', message: 'City' })}
                size={size}
                type="text"
                aria-label={t({ id: 'components.address-form.city.label', message: 'City' })}
                placeholder={t({ id: 'components.address-form.city.label', message: 'City' })}
                value={value?.city ?? ''}
                onChange={(city) => onFieldsChange({ city })}
                autoComplete="address-level2"
                isDisabled={isFieldDisabled('city')}
                error={
                  error?.cityError
                    ? t({
                        id: 'components.address-form.field.city.error',
                        message: 'Please add a city',
                      })
                    : undefined
                }
              />

              {showState && (
                <InputText
                  label={t({ id: 'components.address-form.state.label', message: 'State' })}
                  size={size}
                  type="text"
                  aria-label={t({ id: 'components.address-form.state.label', message: 'State' })}
                  placeholder={t({ id: 'components.address-form.state.label', message: 'State' })}
                  value={value?.state ?? ''}
                  onChange={(state) => onFieldsChange({ state })}
                  autoComplete="address-level1"
                  isDisabled={isFieldDisabled('state')}
                  error={
                    error?.stateError
                      ? t({
                          id: 'components.address-form.field.state.error',
                          message: 'Please add a state',
                        })
                      : undefined
                  }
                />
              )}

              <InputCountry
                label={t({ id: 'components.address-form.country.label', message: 'Country' })}
                aria-label={t({
                  id: 'components.address-form.country.label',
                  message: 'Country',
                })}
                placeholder={t({
                  id: 'components.address-form.country.label',
                  message: 'Country',
                })}
                value={value?.country}
                onChange={(country) => {
                  if (!country) {
                    onFieldsChange({ country: null, state: null });
                  } else {
                    onFieldsChange({
                      country,
                      state: COUNTRIES_REQUIRING_STATE.includes(country) ? value?.state : null,
                    });
                  }
                }}
                isDisabled={isFieldDisabled('country')}
                size={size}
                styleVariant={variant}
                error={
                  error?.countryError
                    ? t({
                        id: 'components.address-form.field.country.error',
                        message: 'Please add a valid country',
                      })
                    : undefined
                }
              />
            </div>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};

export default React.memo(AddressForm);

export const useAddressState = (initialData?: Partial<AddressRaw> | null) =>
  useState<Partial<AddressRaw> | null>(
    initialData
      ? {
          contactEmail: initialData.contactEmail,
          contactName: initialData.contactName,
          line1: initialData.line1,
          line2: initialData.line2,
          zipCode: initialData.zipCode,
          city: initialData.city,
          state: initialData.state,
          country: initialData.country,
        }
      : null
  );

type AddressError = {
  line1Error: boolean;
  zipCodeError: boolean;
  cityError: boolean;
  countryError: boolean;
  stateError: boolean;
  contactEmailError?: boolean;
  contactNameError?: boolean;
  hasError: boolean;
};

export const getAddressError = (address: Partial<AddressRaw> | null, contact: boolean = false) => {
  const line1Error = !address?.line1?.length;
  const zipCodeError = !address?.zipCode?.length;
  const cityError = !address?.city?.length;
  const countryError = !address?.country?.length;
  const stateError =
    !!address?.country &&
    COUNTRIES_REQUIRING_STATE.includes(address.country) &&
    !address.state?.length;

  const contactEmailError =
    !address?.contactEmail || (!!address?.contactEmail && !isEmailValid(address.contactEmail));
  const contactNameError = !address?.contactName?.length;

  return {
    line1Error,
    zipCodeError,
    cityError,
    countryError,
    stateError,
    ...(contact && { contactEmailError, contactNameError }),
    hasError:
      line1Error ||
      zipCodeError ||
      cityError ||
      countryError ||
      stateError ||
      (contact && (contactEmailError || contactNameError)),
  };
};

export const isAddressValid = (
  address: Partial<AddressRaw> | null | undefined,
  contact: boolean = false
): address is AddressRaw => {
  return !getAddressError(address ?? null, contact).hasError;
};

// Source: https://support.shipandco.com/hc/en-us/articles/900004727243-When-the-State-Province-field-of-the-shipping-address-is-required
const COUNTRIES_REQUIRING_STATE = [
  'US', // United States of America
  'CA', // Canada
  'AU', // Australia
  'CN', // China
  'BR', // Brazil
  'MX', // Mexico
  'MY', // Malaysia
  'NZ', // New Zealand
  'KR', // South Korea
] satisfies Alpha2Code[] as string[];
